import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Layout, Table, Button, Empty, Pagination, Modal, App } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import AntdProvider from '@/components/AntdProvider'
import Filter from './Filter'
import Viewer from '../components/ViewerV2'
import CONFIG from '../../constants/config'
import {
  resetFilter,
  setQueryFilter,
  fetchPhotos,
  fetchPhotoWatermarkExcel,
  fetchExtraColumnTitles,
  fetchAiRecognition
} from '../../actions/photoWatermark'
import sensors, { sensorsRole } from '../../utils/sensors'
import desktopLib from '../../utils/desktopLib'
import { useNavigate } from 'react-router-dom'
import { capturePromiseException } from '../../utils/sentryUtils'
import ColumnsSetting from './components/ColumnsSetting'
import baseColumnsData from './lib/baseColumnsData'
import { setLoading, setPaymentVisible } from '../../actions/main'
import {
  setPhotosWatermarksTotalPhotos,
  setTriggerPhotoWaterMarkExcel
} from '../../actions/photoWatermark'
import icon_play from '../../images/icon_play.png'
import {
  buildFetchPhotosParams,
  calcPickedMemberNum,
  createColumns,
  AI_EXTRACT_SCENARIOS,
  PhotoWatermarkItem,
  AiExtractConfig,
  AiRecognitionResult
} from './lib/lib'
import AiExtractLoading from './components/AiExtractLoading'
import createExcelTask, { ExcelTaskType } from '../../app/libs/createExcelTask'
import GroupPhotoViewLimitRow from './components/GroupPhotoViewLimitRow'
import PhotoEmbedTypeDialog, {
  EmbedType
} from './components/PhotoEmbedTypeDialog'
import ContactUS from '../components/ContactUS'

import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { ViewerRef } from '../components/ViewerV2/types'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const pageSize = 50

function PhotoWatermarkContent() {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Redux state
  const isVip = useAppSelector((state) => state.group.isVip)
  const vipInfo = useAppSelector((state) => state.group.vipInfo)
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const filter = useAppSelector((state) => state.photoWatermark.filter)
  const queryFilter = useAppSelector(
    (state) => state.photoWatermark.queryFilter
  )
  const triggerPhotoWaterMarkExcel = useAppSelector(
    (state) => state.photoWatermark.triggerPhotoWaterMarkExcel
  )
  const members = useAppSelector((state) => state.group.members)
  const customerConfig = useAppSelector((state) => state.group.customerConfig)
  const membersCheckedList = useAppSelector(
    (state) => state.photoWatermark.membersCheckedList
  )
  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )
  const isOpenNeteaseShelfReview = useAppSelector(
    (state) => state.group.isOpenNeteaseShelfReview
  )
  const onlyInGroup = useAppSelector((state) => state.group.onlyInGroup)

  // 根据isOpenNeteaseShelfReview决定初始checkedBaseColumns
  const getInitialBaseColumns = useCallback(() => {
    const baseColumns = [
      'userNickname',
      'watermarkName',
      'time',
      'addr',
      'weather',
      'lng',
      'lat',
      'coordinates',
      'altitude'
    ]
    if (isOpenNeteaseShelfReview) {
      baseColumns.splice(2, 0, 'reviewResult', 'reviewOperator')
    }
    return baseColumns
  }, [isOpenNeteaseShelfReview])

  // Local state
  const [photoWatermarks, setPhotoWatermarks] = useState<PhotoWatermarkItem[]>(
    []
  )
  const [checkedBaseColumns, setCheckedBaseColumns] = useState<string[]>(
    getInitialBaseColumns()
  )
  const [extraColumns, setExtraColumns] = useState<string[]>([])
  const [checkedExtraColumns, setCheckedExtraColumns] = useState<string[]>([])
  const [customerLableColumns, setCustomerLableColumns] = useState<string[]>([])
  const [checkedCustomerLableColumns, setCheckedCustomerLableColumns] =
    useState<string[]>([])
  const [checkedAiColumns, setCheckedAiColumns] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPhotos, setTotalPhotos] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [viewPhotos, setViewPhotos] = useState<PhotoWatermarkItem[]>([])
  const [pagingEnd, setPagingEnd] = useState(false)
  const [isGroupPhotoViewLimit, setIsGroupPhotoViewLimit] = useState(false)
  const [aiExtractConfig, setAiExtractConfig] = useState<AiExtractConfig>({
    isEnabled: false,
    selectedScenarios: [],
    extractedPhotos: new Set<string>(),
    isExtracting: false,
    isFilterEnabled: true
  })
  const [aiExtractLoading, setAiExtractLoading] = useState(false)
  const [aiRecognitionResults, setAiRecognitionResults] = useState<
    AiRecognitionResult[]
  >([])
  const [photoEmbedTypeDialogVisible, setPhotoEmbedTypeDialogVisible] =
    useState(false)

  // Refs
  const extractedPagesCacheRef = useRef(
    new Map<number, AiRecognitionResult[]>()
  )
  const lastPageCondRef = useRef('')
  const exportParamsRef = useRef('')
  const viewerRef = useRef<ViewerRef>(null)
  const aiExtractControllerRef = useRef<AbortController | null>(null)
  const prevTriggerRef = useRef(false)
  const queryFilterRef = useRef(queryFilter)
  queryFilterRef.current = queryFilter
  const photoWatermarksRef = useRef(photoWatermarks)
  photoWatermarksRef.current = photoWatermarks

  // 保存AI配置
  const saveAiConfig = useCallback((config: AiExtractConfig) => {
    setAiExtractConfig(config)
  }, [])

  // 获取客户标签列
  const handleFetchCustomerLableColumns = useCallback(
    (photoWatermarksList: PhotoWatermarkItem[]) => {
      const res: string[] = []
      const set = new Set<string>()

      photoWatermarksList.forEach((item) => {
        if (!item.customer?.exist) return
        ;(item.customer.customerLabelGroups || []).forEach(
          ({ labelGroupID, labelGroupName }) => {
            if (!set.has(labelGroupID)) {
              set.add(labelGroupID)
              res.push(labelGroupName)
            }
          }
        )
      })
      return res
    },
    []
  )

  // 埋点方法
  const handleSensorClickItem = useCallback(
    (clickItem, params = {}) => {
      sensors.track('web_click_page_watermark_data', {
        clickItem,
        groupID: groupId,
        role: sensorsRole(myRole),
        track_sign: 'aother.b4366.action.web_click_page_watermark_data',
        requirement_id: 521,
        ...params
      })
    },
    [groupId, myRole]
  )

  // 获取照片数据
  const handleFetchPhotos = useCallback(() => {
    dispatch(
      fetchPhotos(
        buildFetchPhotosParams(
          queryFilterRef.current,
          groupId,
          lastPageCondRef.current,
          onlyInGroup,
          aiExtractConfig
        )
      )
    )
      .then((data) => {
        const newPhotoWatermarks = photoWatermarksRef.current.concat(
          data.photoWatermarks
        )
        const customerLabelColumns =
          handleFetchCustomerLableColumns(newPhotoWatermarks)

        setPhotoWatermarks(newPhotoWatermarks)
        setTotalPhotos((prev) => prev + data.photoWatermarks.length)
        setCustomerLableColumns([...customerLabelColumns])
        setCheckedCustomerLableColumns([...customerLabelColumns])
        setTotalCount(data.total)
        setIsGroupPhotoViewLimit(data.isGroupPhotoViewLimit)

        if (data.isGroupPhotoViewLimit) {
          handleSensorClickItem('getGroupPhotoViewLimit')
        }

        dispatch(setPhotosWatermarksTotalPhotos(data.total))
        lastPageCondRef.current = data.lastPageCond
        exportParamsRef.current = data.exportParams
        if (!data.lastPageCond) setPagingEnd(true)

        if (
          aiExtractConfig &&
          aiExtractConfig.isEnabled &&
          newPhotoWatermarks.length > 0
        ) {
          handleStartAiExtraction(true)
        }
      })
      .catch(capturePromiseException)
  }, [
    dispatch,
    groupId,
    onlyInGroup,
    aiExtractConfig,
    handleFetchCustomerLableColumns,
    handleSensorClickItem
  ])

  // 使用指定AI配置获取照片
  const handleFetchPhotosWithConfig = useCallback(
    (config: AiExtractConfig) => {
      dispatch(
        fetchPhotos(
          buildFetchPhotosParams(
            queryFilterRef.current,
            groupId,
            lastPageCondRef.current,
            config
          )
        )
      )
        .then((data) => {
          const newPhotoWatermarks = !lastPageCondRef.current
            ? data.photoWatermarks
            : photoWatermarksRef.current.concat(data.photoWatermarks)
          const customerLabelColumns =
            handleFetchCustomerLableColumns(newPhotoWatermarks)

          setPhotoWatermarks(newPhotoWatermarks)
          setTotalPhotos(
            !lastPageCondRef.current
              ? data.photoWatermarks.length
              : totalPhotos + data.photoWatermarks.length
          )
          setCustomerLableColumns([...customerLabelColumns])
          setCheckedCustomerLableColumns([...customerLabelColumns])
          setTotalCount(data.total)
          setIsGroupPhotoViewLimit(data.isGroupPhotoViewLimit)

          if (data.isGroupPhotoViewLimit) {
            handleSensorClickItem('getGroupPhotoViewLimit')
          }

          dispatch(setPhotosWatermarksTotalPhotos(data.total))
          lastPageCondRef.current = data.lastPageCond
          exportParamsRef.current = data.exportParams
          if (!data.lastPageCond) setPagingEnd(true)

          if (config && config.isEnabled && newPhotoWatermarks.length > 0) {
            handleStartAiExtraction()
          }
        })
        .catch(capturePromiseException)
    },
    [
      dispatch,
      groupId,
      totalPhotos,
      handleFetchCustomerLableColumns,
      handleSensorClickItem
    ]
  )

  // 获取额外列
  const handleFetchExtraColumns = useCallback(() => {
    dispatch(
      fetchExtraColumnTitles(
        buildFetchPhotosParams(
          queryFilterRef.current,
          groupId,
          '',
          aiExtractConfig
        )
      )
    )
      .then((data) => {
        if (data.status === 0) {
          setExtraColumns(data.list)
          setCheckedExtraColumns(data.list)
        }
      })
      .catch(capturePromiseException)
  }, [dispatch, groupId, aiExtractConfig])

  // 重置并重新获取数据
  const resetAndFetchPhotos = useCallback(() => {
    setPhotoWatermarks([])
    photoWatermarksRef.current = []
    setTotalPhotos(0)
    setCurrentPage(1)
    setIsGroupPhotoViewLimit(false)
    lastPageCondRef.current = ''
    setPagingEnd(false)
    handleFetchPhotos()
  }, [handleFetchPhotos])

  // 重置并重新获取数据（使用指定的AI配置）
  const resetAndFetchPhotosWithConfig = useCallback(
    (config) => {
      setPhotoWatermarks([])
      photoWatermarksRef.current = []
      setTotalPhotos(0)
      setCurrentPage(1)
      setIsGroupPhotoViewLimit(false)
      lastPageCondRef.current = ''
      setPagingEnd(false)
      handleFetchPhotosWithConfig(config)
    },
    [handleFetchPhotosWithConfig]
  )

  // AI提取保存
  const handleAiExtractSave = useCallback(
    (selectedOptions: string[]) => {
      const updatedConfig: AiExtractConfig = {
        ...aiExtractConfig,
        isEnabled: true,
        selectedScenarios: selectedOptions,
        extractedPhotos: new Set<string>(),
        isFilterEnabled: true
      }

      const aiColumns: string[] = []
      selectedOptions.forEach((scenarioId) => {
        const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
        if (scenario && scenario.columns) {
          scenario.columns.forEach((column) => {
            aiColumns.push(column.dataIndex)
          })
        }
      })

      setAiExtractConfig(updatedConfig)
      setCheckedAiColumns(aiColumns)
      saveAiConfig(updatedConfig)

      extractedPagesCacheRef.current.clear()

      resetAndFetchPhotosWithConfig(updatedConfig)

      message.success('AI提取配置已保存，正在重新加载数据...')
    },
    [aiExtractConfig, saveAiConfig, resetAndFetchPhotosWithConfig]
  )

  // 关闭AI提取功能
  const handleCloseAiExtract = useCallback(() => {
    const updatedConfig: AiExtractConfig = {
      isEnabled: false,
      selectedScenarios: [],
      extractedPhotos: new Set<string>(),
      isExtracting: false,
      isFilterEnabled: true
    }

    setAiExtractConfig(updatedConfig)
    setCheckedAiColumns([])
    saveAiConfig(updatedConfig)

    extractedPagesCacheRef.current.clear()

    resetAndFetchPhotosWithConfig(updatedConfig)

    message.success('AI提取功能已关闭，正在重新加载数据...')
  }, [saveAiConfig, resetAndFetchPhotosWithConfig])

  // 重新提取功能
  const handleRetryExtraction = useCallback(() => {
    if (!photoWatermarks || photoWatermarks.length === 0) {
      message.warning('没有可提取的照片')
      return
    }

    extractedPagesCacheRef.current.clear()

    const updatedConfig: AiExtractConfig = {
      ...aiExtractConfig,
      extractedPhotos: new Set<string>()
    }

    saveAiConfig(updatedConfig)

    handleFetchPhotos()

    message.info('开始重新提取...')
  }, [photoWatermarks, aiExtractConfig, saveAiConfig, handleFetchPhotos])

  // 开始AI提取
  const handleStartAiExtraction = useCallback(
    (forceRefresh = false) => {
      if (!forceRefresh && extractedPagesCacheRef.current.has(currentPage)) {
        const cachedResults = extractedPagesCacheRef.current.get(currentPage)
        setAiRecognitionResults(cachedResults || [])
        setAiExtractLoading(false)
        return
      }

      if (forceRefresh && extractedPagesCacheRef.current.has(currentPage)) {
        extractedPagesCacheRef.current.delete(currentPage)
      }

      const dataSource = photoWatermarks.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      )

      const photoIDs = dataSource.map((photo) => photo.seq).filter(Boolean)

      if (photoIDs.length === 0) {
        message.warning('当前页面没有照片可以提取')
        return
      }

      setAiExtractLoading(true)

      aiExtractControllerRef.current = new AbortController()

      dispatch(
        fetchAiRecognition(
          {
            groupID: groupId,
            photoIDs: photoIDs
          },
          aiExtractControllerRef.current.signal
        )
      )
        .then((response) => {
          if (response && response.code === 200 && response.data) {
            handleSensorClickItem('提取成功')
            const results = response.data.aiRecognitionResults || []

            extractedPagesCacheRef.current.set(currentPage, results)

            setAiRecognitionResults(results)
            setAiExtractLoading(false)
            message.destroy()
            message.success('本页数据已提取完成')
          } else {
            message.destroy()
            message.error(response.msg)
            setAiExtractLoading(false)
          }
        })
        .catch((error) => {
          console.log('error', error)
          message.destroy()
          message.error('AI提取失败')
          setAiExtractLoading(false)
        })
    },
    [dispatch, groupId, currentPage, photoWatermarks, handleSensorClickItem]
  )

  // 取消AI提取
  const handleCancelAiExtraction = useCallback(() => {
    handleSensorClickItem('取消加载')
    if (aiExtractControllerRef.current) {
      aiExtractControllerRef.current.abort()
      aiExtractControllerRef.current = null
    }
    setAiExtractLoading(false)
    message.info('已取消AI提取')
  }, [handleSensorClickItem])

  // 切换AI筛选
  const handleToggleAiFilter = useCallback(
    (checked: boolean) => {
      const updatedConfig = {
        ...aiExtractConfig,
        isFilterEnabled: checked
      }

      setAiExtractConfig(updatedConfig)
      setPhotoWatermarks([])
      photoWatermarksRef.current = []
      setTotalPhotos(0)
      setCurrentPage(1)
      setIsGroupPhotoViewLimit(false)
      saveAiConfig(updatedConfig)

      lastPageCondRef.current = ''
      setPagingEnd(false)

      const params = buildFetchPhotosParams(
        queryFilterRef.current,
        groupId,
        '',
        updatedConfig
      )

      dispatch(fetchPhotos(params))
        .then((data) => {
          const newPhotoWatermarks = data.photoWatermarks
          const customerLabelColumns =
            handleFetchCustomerLableColumns(newPhotoWatermarks)

          setPhotoWatermarks(newPhotoWatermarks)
          setTotalPhotos(data.photoWatermarks.length)
          setCustomerLableColumns([...customerLabelColumns])
          setCheckedCustomerLableColumns([...customerLabelColumns])
          setTotalCount(data.total)
          setIsGroupPhotoViewLimit(data.isGroupPhotoViewLimit)

          if (data.isGroupPhotoViewLimit) {
            handleSensorClickItem('getGroupPhotoViewLimit')
          }

          dispatch(setPhotosWatermarksTotalPhotos(data.total))
          lastPageCondRef.current = data.lastPageCond
          exportParamsRef.current = data.exportParams
          if (!data.lastPageCond) setPagingEnd(true)
        })
        .catch(capturePromiseException)
    },
    [
      dispatch,
      aiExtractConfig,
      groupId,
      saveAiConfig,
      handleFetchCustomerLableColumns,
      handleSensorClickItem
    ]
  )

  // 提交筛选
  const handleSubmit = useCallback(() => {
    lastPageCondRef.current = ''
    exportParamsRef.current = ''
    setPagingEnd(false)
    setCurrentPage(1)
    setTotalPhotos(0)
    setPhotoWatermarks([])
    photoWatermarksRef.current = []
    setIsGroupPhotoViewLimit(false)
    // 先同步更新 ref，确保 handleFetchPhotos 能读到最新的 queryFilter
    queryFilterRef.current = filter
    dispatch(setQueryFilter(filter)).then(() => {
      handleFetchPhotos()
      handleFetchExtraColumns()
    })
  }, [dispatch, filter, handleFetchPhotos, handleFetchExtraColumns])

  // 重置筛选
  const handleReset = useCallback(() => {
    lastPageCondRef.current = ''
    exportParamsRef.current = ''
    setPagingEnd(false)
    setCurrentPage(1)
    setTotalPhotos(0)
    setPhotoWatermarks([])
    photoWatermarksRef.current = []
    setIsGroupPhotoViewLimit(false)
    dispatch(resetFilter()).then((resetQueryFilter) => {
      // 同步更新 ref，确保 handleFetchPhotos 能读到重置后的 queryFilter
      queryFilterRef.current = resetQueryFilter
      handleFetchPhotos()
    })

    handleSensorClickItem('reset')
  }, [dispatch, handleFetchPhotos, handleSensorClickItem])

  // 列变化
  const handleColumnsChange = useCallback(
    (
      baseColumns: string[],
      extraColumnsParam: string[],
      customerLableColumnsParam: string[],
      checkedAiColumnsParam: string[] = []
    ) => {
      let finalBaseColumns = baseColumns
      if (!isOpenNeteaseShelfReview) {
        finalBaseColumns = baseColumns.filter(
          (col) => col !== 'reviewResult' && col !== 'reviewOperator'
        )
      }

      setCheckedBaseColumns(finalBaseColumns)
      setCheckedExtraColumns(extraColumnsParam)
      setCheckedCustomerLableColumns(customerLableColumnsParam)
      setCheckedAiColumns(checkedAiColumnsParam)
      localStorage.setItem(
        'photoWatermarksBaseColumns',
        JSON.stringify(finalBaseColumns)
      )
    },
    [isOpenNeteaseShelfReview]
  )

  // 页码变化
  const handlePageChange = useCallback(
    (page: number) => {
      if (page > Math.ceil(totalPhotos / pageSize) - 3 && !pagingEnd) {
        handleFetchPhotos()
      }
      setCurrentPage(page)
      if (aiExtractConfig && aiExtractConfig.isEnabled) {
        setTimeout(() => {
          handleStartAiExtraction()
        }, 0)
      }
    },
    [
      totalPhotos,
      pagingEnd,
      aiExtractConfig,
      handleFetchPhotos,
      handleStartAiExtraction
    ]
  )

  // 计算选中成员数
  const handleCalcMemberPickedValue = useCallback(() => {
    return calcPickedMemberNum(filter, pickerUserCount)
  }, [filter, pickerUserCount])

  // 获取导出参数
  const getExportParams = useCallback(() => {
    const defaultTitles = baseColumnsData
      .filter((column) => checkedBaseColumns.includes(column.dataIndex))
      .map((column) => column.title)

    const aiTitles: string[] = []
    if (
      aiExtractConfig &&
      aiExtractConfig.isEnabled &&
      aiExtractConfig.selectedScenarios &&
      checkedAiColumns.length > 0
    ) {
      aiExtractConfig.selectedScenarios.forEach((scenarioId) => {
        const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
        if (scenario && scenario.columns) {
          scenario.columns.forEach((column) => {
            if (checkedAiColumns.includes(column.dataIndex)) {
              aiTitles.push(column.title)
            }
          })
        }
      })
    }

    const extendTitles = [...checkedExtraColumns, ...aiTitles]

    const params: {
      groupID: string
      exportParams: string
      defaultTitles: string[]
      extendTitles: string[]
      type?: number
      formatType?: number
      extra?: { orderNo: string | null }
    } = {
      groupID: groupId,
      exportParams: exportParamsRef.current,
      defaultTitles,
      extendTitles
    }
    return params
  }, [
    groupId,
    checkedBaseColumns,
    checkedExtraColumns,
    checkedAiColumns,
    aiExtractConfig
  ])

  // 导出Excel
  const handleExcel = useCallback(
    (isSinglePaid = false, embedType = EmbedType.IMAGE_EMBED) => {
      if (!isVip && !isSinglePaid) {
        dispatch(setPaymentVisible(true, 'watermarkDataChooseDateExcel'))
        return
      }

      handleSensorClickItem('download', {
        photosNum: photoWatermarks.length
      })

      if (totalCount > CONFIG.photoWatermarksMaxDownloadLimit) {
        Modal.info({
          title: `团队会员最多可支持${CONFIG.photoWatermarksMaxDownloadLimit}条数据的下载`,
          content: (
            <div>
              <p>请您适当缩减筛选条件后下载</p>
            </div>
          ),
          okText: '我知道了'
        })
        return
      }

      const params = getExportParams()
      params.type = embedType === EmbedType.NO_IMAGE ? 0 : 1
      params.formatType = embedType === EmbedType.NO_IMAGE ? 0 : embedType
      if (isSinglePaid) {
        params.extra = {
          orderNo: localStorage.getItem('orderNo')
        }
      }
      dispatch(fetchPhotoWatermarkExcel(params))
        .then((data) => {
          if (data.exportResult !== 1) {
            if (data.exportResult === -202) {
              Modal.warning({
                title: '提示',
                content: '选择时间段内无照片'
              })
              return
            }
            message.error(data.msg)
            return
          }
          createExcelTask({
            membersNum: handleCalcMemberPickedValue(),
            exportType: ExcelTaskType.PHOTO_STATISTICS,
            startDate: queryFilter!.times[0].format(CONFIG.timeFormat),
            endDate: queryFilter!.times[1].format(CONFIG.timeFormat),
            finished: data.exportResult,
            fileName: data.fileName,
            url: data.exportURL,
            contentNumber: data.contentNumber
          })
          if (isSinglePaid) {
            navigate('/attendance/export-list?fromPlace=watermarkData', {
              replace: true
            })
            return
          }
          window.open('/attendance/export-list?fromPlace=watermarkData')
        })
        .catch((err) => {
          capturePromiseException(err)
        })

      queryFilter &&
        sensors.track('web_workgroup_watermarkdatapage_click_download', {
          start_date: queryFilter.times[0].format(CONFIG.timeFormat),
          end_date: queryFilter.times[1].format(CONFIG.timeFormat),
          memberNum: handleCalcMemberPickedValue(),
          allMemberNum: members.length,
          isContainPhoto: embedType !== EmbedType.NO_IMAGE,
          isGroupVIP: isVip
        })
    },
    [
      dispatch,
      isVip,
      photoWatermarks.length,
      totalCount,
      getExportParams,
      handleCalcMemberPickedValue,
      queryFilter,
      members.length,
      handleSensorClickItem
    ]
  )

  // 查看照片
  const handleViewPhoto = useCallback(
    (record: PhotoWatermarkItem) => {
      const index = photoWatermarks.findIndex((item) => item.seq === record.seq)
      viewerRef.current?.view(index)
      handleSensorClickItem('photo')
    },
    [photoWatermarks, handleSensorClickItem]
  )

  // componentDidMount
  useEffect(() => {
    // 先同步更新 ref，确保 handleFetchPhotos 能读到最新的 queryFilter
    queryFilterRef.current = filter
    dispatch(setQueryFilter(filter)).then(() => {
      handleFetchPhotos()
      handleFetchExtraColumns()
    })
    const columns = localStorage.getItem('photoWatermarksBaseColumns')
    if (columns) {
      const savedColumns = JSON.parse(columns)

      if (isOpenNeteaseShelfReview) {
        const hasReviewResult = savedColumns.includes('reviewResult')
        const hasReviewOperator = savedColumns.includes('reviewOperator')

        if (!hasReviewResult || !hasReviewOperator) {
          const watermarkNameIndex = savedColumns.indexOf('watermarkName')
          if (watermarkNameIndex !== -1) {
            if (!hasReviewResult) {
              savedColumns.splice(watermarkNameIndex + 1, 0, 'reviewResult')
            }
            if (!hasReviewOperator) {
              const reviewResultIndex = savedColumns.indexOf('reviewResult')
              savedColumns.splice(reviewResultIndex + 1, 0, 'reviewOperator')
            }
          }
        }
      } else {
        const filteredColumns = savedColumns.filter(
          (col) => col !== 'reviewResult' && col !== 'reviewOperator'
        )
        setCheckedBaseColumns(filteredColumns)
        return
      }

      setCheckedBaseColumns(savedColumns)
    }
    handleSensorClickItem('enterPage')
  }, []) // 空依赖数组，只在mount时执行

  // componentDidUpdate for triggerPhotoWaterMarkExcel
  useEffect(() => {
    if (triggerPhotoWaterMarkExcel && !prevTriggerRef.current) {
      handleExcel(true)
      dispatch(setTriggerPhotoWaterMarkExcel(false))
    }
    prevTriggerRef.current = triggerPhotoWaterMarkExcel
  }, [triggerPhotoWaterMarkExcel, handleExcel, dispatch])

  // render
  const dataSource: PhotoWatermarkItem[] = photoWatermarks.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  if (
    pagingEnd &&
    (currentPage === Math.ceil(totalPhotos / pageSize) || totalPhotos === 0) &&
    isGroupPhotoViewLimit
  ) {
    dataSource.push({
      seq: 'groupPhotoViewLimit'
    })
  }

  const columns = createColumns(dataSource, {
    extraColumnsTitles: extraColumns,
    checkedBaseColumns,
    checkedExtraColumns,
    customerConfig: customerConfig,
    limitRowContent: (
      <GroupPhotoViewLimitRow handleSensorClickItem={handleSensorClickItem} />
    ),
    aiExtractConfig: aiExtractConfig,
    checkedAiColumns: checkedAiColumns,
    aiRecognitionResults: aiRecognitionResults
  })

  // 照片/视频列
  const photoColumn: any = {
    title: '照片/视频',
    width: 110,
    fixed: true,
    dataIndex: 'smallURL',
    onCell: (record: PhotoWatermarkItem) => {
      if (record.seq === 'groupPhotoViewLimit') {
        return {
          style: {
            padding: 0
          }
        }
      }
    },
    render: (text: string, record: PhotoWatermarkItem) => {
      if (record.seq === 'groupPhotoViewLimit') {
        return null
      }

      if (record.isLimitViewByPhotoCount) {
        const vipKind = vipInfo && vipInfo.vip_kind
        const isSVip = isVip && vipKind === CONFIG.VIP_KIND.SVIP
        const isVVip = isVip && vipKind === CONFIG.VIP_KIND.VVIP

        return (
          <div
            className="photo-thumbnail"
            style={{
              position: 'relative',
              backgroundImage: `url(${text})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {record.mediaType === 1 && (
              <img
                src={icon_play}
                alt=""
                style={{ position: 'relative', zIndex: 5 }}
              />
            )}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${text})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(3px)',
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />
            {isSVip ? (
              <ContactUS
                fromPage="photo_limit_vvip"
                artificial={false}
                style={{ display: 'block' }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#ffffff',
                    fontSize: 14,
                    fontWeight: 600,
                    zIndex: 20,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                    cursor: 'pointer'
                  }}
                >
                  空间已满
                </div>
              </ContactUS>
            ) : (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  if (isVVip) {
                    dispatch(
                      setPaymentVisible(
                        true,
                        'webGroupPhotoStorageVip',
                        CONFIG.VIP_KIND.SVIP,
                        false,
                        true,
                        false
                      )
                    )
                    return
                  }

                  if (isVip) {
                    dispatch(
                      setPaymentVisible(
                        true,
                        'webGroupPhotoStorageVip',
                        CONFIG.VIP_KIND.VVIP,
                        false,
                        false,
                        true
                      )
                    )
                    return
                  }

                  dispatch(setPaymentVisible(true, 'webGroupPhotoStorageVip'))
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: 600,
                  zIndex: 20,
                  pointerEvents: 'auto',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                  cursor: 'pointer'
                }}
              >
                空间已满
              </div>
            )}
          </div>
        )
      }

      return (
        <div
          className="photo-thumbnail"
          style={{ backgroundImage: `url(${text})` }}
          onClick={() => handleViewPhoto(record)}
        >
          {record.mediaType === 1 && <img src={icon_play} alt="" />}
        </div>
      )
    }
  }

  columns.unshift(photoColumn)

  const totalWidth = columns.reduce(
    (totalWidthVal, column) =>
      totalWidthVal + (typeof column.width === 'number' ? column.width : 0),
    0
  )

  if (totalWidth <= window.innerWidth - 230 - 110) {
    columns.forEach((column: any, index) => {
      if (index > 0) {
        column.width = undefined
      }
    })
  }

  return (
    <Layout className="Watermark-Data">
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          overflow: 'visible'
        }}
        title="照片台账表"
        extra={[
          !desktopLib.isDesktop && (
            <Button
              key="form-entry"
              type="primary"
              style={{ overflow: 'visible', fontSize: 14, color: '#fff' }}
              onClick={() => {
                window.open(
                  'https://photoform.5181688.com/?source=web_taizhang',
                  '_blank'
                )
              }}
            >
              任务表单
            </Button>
          ),
          <Button
            key="1"
            type="primary"
            icon={
              <DownloadOutlined
                style={{ marginRight: -3, fontSize: 14, color: '#fff' }}
              />
            }
            style={{ overflow: 'visible', fontSize: 14, color: '#fff' }}
            onClick={() => {
              handleSensorClickItem('exportTable')
              setPhotoEmbedTypeDialogVisible(true)
            }}
          >
            导出表格
          </Button>
        ]}
      />
      <Content className="content">
        <PhotoEmbedTypeDialog
          visible={photoEmbedTypeDialogVisible}
          totalCount={totalCount}
          onCancel={() => setPhotoEmbedTypeDialogVisible(false)}
          onOk={(embedType) => {
            handleExcel(false, embedType)
            setPhotoEmbedTypeDialogVisible(false)
          }}
        />
        <Filter onSubmit={handleSubmit} onReset={handleReset} />
        <div className="table-wrapper">
          <ColumnsSetting
            checkedBaseColumns={checkedBaseColumns}
            extraColumns={extraColumns}
            customerLableColumns={customerLableColumns}
            checkedExtraColumns={checkedExtraColumns}
            checkedCustomerLableColumns={checkedCustomerLableColumns}
            checkedAiColumns={checkedAiColumns}
            onChange={handleColumnsChange}
            handleSensorClickItem={handleSensorClickItem}
            isOpenNeteaseShelfReview={isOpenNeteaseShelfReview}
            aiExtractConfig={aiExtractConfig}
            onAiExtractSave={handleAiExtractSave}
            onCloseAiExtract={handleCloseAiExtract}
            onRetryExtraction={handleRetryExtraction}
            onStartAiExtraction={handleStartAiExtraction}
            onCancelAiExtraction={handleCancelAiExtraction}
            onToggleAiFilter={handleToggleAiFilter}
          />
          {dataSource.length ? (
            <>
              <Table
                bordered
                pagination={false}
                scroll={{
                  x: '100%',
                  y: window.innerHeight - 460,
                  scrollToFirstRowOnChange: true
                }}
                rowKey="seq"
                dataSource={dataSource}
                columns={columns as any}
              ></Table>
              <div className="pagination-wrapper">
                <span className="total">共{totalCount}条</span>
                {totalPhotos > 0 && (
                  <Pagination
                    current={currentPage}
                    total={totalPhotos}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    showQuickJumper
                    onChange={handlePageChange}
                  />
                )}
              </div>
            </>
          ) : (
            <Empty style={{ marginTop: '300px' }} />
          )}
        </div>
        <Viewer
          ref={viewerRef}
          photos={photoWatermarks}
          canDelete={false}
          isShowComments={false}
          fromPlace={'watermarkData'}
          autoShowInfoPanel={true}
        />

        <AiExtractLoading
          visible={aiExtractLoading}
          onCancel={handleCancelAiExtraction}
          loadingText="正在提取AI数据，点击可取消加载..."
        />
      </Content>
    </Layout>
  )
}

// 使用 AntdProvider 包裹，确保 App.useApp() 能正常工作
function PhotoWatermark() {
  return (
    <AntdProvider>
      <PhotoWatermarkContent />
    </AntdProvider>
  )
}

export default PhotoWatermark
